import React, { useState, useMemo, useCallback } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useUploadBrandPricingRulesMutation } from '@/services/innovorder/pricingRule/pricingRule.endpoints';
import { getUserToken } from '@/redux/user';
import { useSelector } from 'react-redux';
import { StepData } from '@/components/atoms/Wizard/Wizard';
import { computeText } from '@/locales/utils';
import { useIntl } from 'react-intl';
import { UploadPricingRulesFormValues } from './UploadModal.type';
import { UploadStep } from './UploadStep';
import { SuccessStep } from './SuccessStep';
import { formatMessage } from './UploadModal.utils';

type UploadModalVMProps = {
    onHide: () => void;
    brandId: string;
};

export const useUploadModalVM = ({ onHide, brandId }: UploadModalVMProps) => {
    const intl = useIntl();
    const token = useSelector(getUserToken);
    const [currentStep, setCurrentStep] = useState(0);
    const [uploadPricingRules, { isLoading, data }] = useUploadBrandPricingRulesMutation();

    const message = useMemo(() => {
        if (!data) {
            return '';
        }

        return formatMessage(intl, data.data);
    }, [data, intl]);

    const form: UseFormReturn<UploadPricingRulesFormValues> = useForm<UploadPricingRulesFormValues>({
        mode: 'all',
        defaultValues: {
            file: null,
        },
    });

    const onFormValidate = async () => {
        const { getValues } = form;
        const { file } = getValues();

        if (file) {
            const formData = new FormData();
            formData.append('file', file);
            await uploadPricingRules({
                brandId: Number(brandId),
                formData,
                token,
            });
            setCurrentStep(1);
        }
    };

    const handleHide = useCallback(() => {
        if (!isLoading) {
            setCurrentStep(0);
            onHide();
        }

        if (data?.state === 'SUCCESS') {
            window.location.reload();
        }
        return true;
    }, [isLoading, onHide, setCurrentStep, data]);

    const steps: StepData[] = [
        {
            title: 'pricingRule.modal.import.report.title',
            component: <UploadStep brandId={brandId} />,
            showHeader: false,
            showNext: false,
            showValidate: true,
            onValidate: onFormValidate,
        },
        {
            title: 'pricingRule.modal.import.report.title',
            component: <SuccessStep state={data?.state} message={message} />,
            showHeader: true,
            showValidate: false,
            showPrevious: false,
            cancelLabel: computeText(intl, 'button.ok'),
            onCancel: handleHide,
        },
    ];

    React.useEffect(() => {
        form.watch(['file']);
    }, [form]);

    return {
        form,
        canValidate: form.formState.isValid,
        currentStep,
        steps,
        handleHide,
        isLoading,
        onFormValidate,
    };
};
